/**
 * Renderer Utilities — Shared helpers for question renderers.
 *
 * Extracted during practice audit R2-DUP1 to deduplicate hexToRgba
 * and FALLBACK_COLOR across ErrorSpotting, SentenceErrorJudge, and
 * TrueFalseSeries renderers.
 */

import type { CSSProperties } from 'react';

/**
 * Extended CSSProperties that allows Tailwind CSS custom properties (--tw-*)
 * in inline style objects without @ts-ignore. (R3-T2)
 */
export type TailwindCSSProperties = CSSProperties & Record<`--${string}`, string>;

/** Default module accent color (indigo-500) used when no moduleColor is provided */
export const FALLBACK_COLOR = '#6366F1';

/** Convert a hex color string to an rgba() CSS value */
export function hexToRgba(hex: string, alpha: number): string {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return `rgba(99, 102, 241, ${alpha})`; // fallback to indigo-500
  }

  // Expand shorthand hex (e.g., #123 to #112233)
  let cleanHex = hex.slice(1);
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(char => char + char).join('');
  }
  if (cleanHex.length !== 6) {
    return `rgba(99, 102, 241, ${alpha})`;
  }
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return `rgba(99, 102, 241, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Shared Tailwind class for option-buttons under chart visualisations
 * (BarChart / LineChart / PieChart). Guarantees Fitts' Law ≥44×44px
 * touch-target on mobile, with a more compact desktop variant.
 *
 * STU UX Audit (Ronde 2, 2026) — CC-2 / P0:
 * Old `px-3 py-1.5 text-sm` (~28px tall) failed Fitts on touch for
 * 9-12-year-olds reading STU charts. New class: `min-h-[44px]` mobile,
 * `md:min-h-[36px]` desktop where space is tighter.
 */
export const CHART_OPTION_BUTTON_CLASS = 'px-4 py-3 md:px-3 md:py-2 min-h-[44px] md:min-h-[36px] rounded-md text-sm md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';